import { useEffect, useState, useCallback, useRef } from 'react'
import { sdk } from '@/lib/sdk'
import { useRealtime } from '@/lib/realtime/RealtimeProvider'
import type { Event } from '@/types/events'

interface EventNotification {
  id: string
  type: 'live_started' | 'upcoming' | 'reminder'
  event: Event
  timestamp: Date
  shown: boolean
}

interface UseEventNotificationsOptions {
  spaceId?: string
  enabled?: boolean
  /** How often the loaded list is checked again for "Começa em 1 h" and the 6 h reminder (local clock, no request). */
  checkInterval?: number
}

const HOUR_MS = 60 * 60 * 1000
const PRIORITY: Record<EventNotification['type'], number> = { live_started: 0, upcoming: 1, reminder: 2 }

/**
 * Toasts of the Events module: "ao vivo agora", "começa em 1 h" and the 6 h reminders.
 * F-110: the list is fetched once and again only when the socket says an event changed
 * (`event.live`, `events` in `community`), after a reconnect, or on the slow fallback poll
 * while the socket is down. "Começa em 1 h" and the reminders run on the app's clock over
 * the list already loaded. Each toast is raised once.
 */
export function useEventNotifications({
  spaceId,
  enabled = true,
  checkInterval = 60_000
}: UseEventNotificationsOptions = {}) {
  const [notifications, setNotifications] = useState<EventNotification[]>([])
  const [events, setEvents] = useState<Event[]>([])
  const raisedRef = useRef(new Set<string>())
  const active = Boolean(enabled && spaceId)

  const evaluate = useCallback((list: Event[]) => {
    const now = new Date()
    const fresh: EventNotification[] = []
    const raise = (id: string, type: EventNotification['type'], event: Event) => {
      if (raisedRef.current.has(id)) return
      raisedRef.current.add(id)
      fresh.push({ id, type, event, timestamp: now, shown: false })
    }
    for (const event of list) {
      if (event.isLive) raise(`${event.id}-live-${event.liveStartedAt || ''}`, 'live_started', event)
      const hoursUntil = (new Date(event.startTime).getTime() - now.getTime()) / HOUR_MS
      if (hoursUntil > 0 && hoursUntil <= 1) raise(`${event.id}-upcoming`, 'upcoming', event)
      /* The 6 h reminders (6, 12 and 18 h before); inside the last hour "começa em breve" covers it. */
      if (hoursUntil > 1 && hoursUntil <= 24 && Math.floor(hoursUntil) % 6 === 0) {
        raise(`${event.id}-reminder-${Math.floor(hoursUntil)}`, 'reminder', event)
      }
    }
    /* Live first, then "começa em breve", then reminders, so a busy agenda never pushes the
       urgent ones out of the queue (it keeps 10). */
    if (fresh.length) {
      setNotifications((prev) => [...fresh, ...prev]
        .sort((x, y) => PRIORITY[x.type] - PRIORITY[y.type])
        .slice(0, 10))
    }
  }, [])

  const load = useCallback(async () => {
    if (!spaceId) return
    try {
      const list = await sdk.events.list({ spaceId })
      if (!list) return
      setEvents(list)
      evaluate(list)
    } catch (error) {
      console.error('Error checking event notifications:', error)
    }
  }, [spaceId, evaluate])

  useEffect(() => {
    raisedRef.current = new Set()
    setNotifications([])
    setEvents([])
    if (active) void load()
  }, [active, load])

  useRealtime(['community'], () => { void load() }, { enabled: active, topics: ['event.live', 'events'] })

  /* The local clock: an event entering the 1 h window (or a reminder hour) toasts without a request. */
  useEffect(() => {
    if (!active || !events.length) return
    const timer = window.setInterval(() => evaluate(events), checkInterval)
    return () => window.clearInterval(timer)
  }, [active, events, checkInterval, evaluate])

  const markAsShown = useCallback((notificationId: string) => {
    setNotifications(prev =>
      prev.map(n =>
        n.id === notificationId ? { ...n, shown: true } : n
      )
    )
  }, [])

  const clearNotification = useCallback((notificationId: string) => {
    setNotifications(prev => prev.filter(n => n.id !== notificationId))
  }, [])

  const clearAll = useCallback(() => {
    setNotifications([])
  }, [])

  const unshownNotifications = notifications.filter(n => !n.shown)

  return {
    notifications,
    unshownNotifications,
    hasUnshown: unshownNotifications.length > 0,
    markAsShown,
    clearNotification,
    clearAll
  }
}
